import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  ArrowUturnRightIcon,
  XCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Transition, Field, Label } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Fragment, useCallback } from 'react';
import { tasksApi } from '../api/endpoints';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { formatFileSize, formatDate, getStatusColor, getStatusText } from '../utils/helpers';
import { useTasksStore } from '../stores/tasksStore';
import Listbox from '../components/ui/Listbox';
import { showError, showSuccess } from '../stores/errorStore';
import TaskDetailPanel from '../components/TaskDetailPanel';
import SpotlightCard from '../components/ui/SpotlightCard';
import { InteractiveList } from '../components/ui/PhysicsList';
import PageHeader from '../components/ui/PageHeader';

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'queued', label: '排队中' },
  { value: 'downloading', label: '下载中' },
  { value: 'uploading', label: '上传中' },
  { value: 'completed', label: '已完成' },
  { value: 'failed', label: '失败' },
  { value: 'cancelled', label: '已取消' },
];

export default function Tasks() {
  const [searchParams] = useSearchParams();
  const { tasks, loading, pagination, isCacheValid, setTasks, setLoading } = useTasksStore();
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<{
    search: string;
    status: string;
    userId: string;
    startDate?: number;
    endDate?: number;
    page: number;
    pageSize: number;
  }>({
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    userId: searchParams.get('userId') || '',
    startDate: searchParams.get('startDate') ? parseInt(searchParams.get('startDate')!) : undefined,
    endDate: searchParams.get('endDate') ? parseInt(searchParams.get('endDate')!) : undefined,
    page: parseInt(searchParams.get('page') || '1'),
    pageSize: 20,
  });
  const [showFilters, setShowFilters] = useState(!!(searchParams.get('status') || searchParams.get('userId') || searchParams.get('startDate') || searchParams.get('endDate')));
  const [exportLoading, setExportLoading] = useState(false);
  const [detailTask, setDetailTask] = useState<any | null>(null);
  // 集成停滞任务 UI (panel + batch reset)
  const [showStalledPanel, setShowStalledPanel] = useState(false);
  const [stalledTasks, setStalledTasks] = useState<any[]>([]);
  const [loadingStalled, setLoadingStalled] = useState(false);
  const [selectedStalledIds, setSelectedStalledIds] = useState<Set<string>>(new Set());

  const loadStalledTasks = async () => {
    try {
      setLoadingStalled(true);
      // 5分钟超时可作为示例
      const resp = await tasksApi.getStalledTasks(5 * 60 * 1000);
      if (resp.success && resp.data?.tasks) {
        setStalledTasks(resp.data.tasks);
      } else {
        setStalledTasks([]);
      }
    } catch (err) {
      console.error('Failed to load stalled tasks:', err);
    } finally {
      setLoadingStalled(false);
    }
  };

  const toggleStalledSelect = (id: string, checked: boolean) => {
    const next = new Set(selectedStalledIds);
    if (checked) next.add(id); else next.delete(id);
    setSelectedStalledIds(next);
  };

  const handleResetSelected = async () => {
    if (selectedStalledIds.size === 0) { return; }
    const ids = Array.from(selectedStalledIds);
    try {
      const resp = await tasksApi.resetStalledTasks(ids, 5 * 60 * 1000);
      if (resp.success) {
        // 清空选中并刷新
        setSelectedStalledIds(new Set());
        loadStalledTasks();
        loadTasks();
        // 也可以提示成功
        showSuccess(`已重置 ${resp.data?.count ?? ids.length} 条停滞任务`);
      }
    } catch (e) {
      console.error('Reset stalled tasks failed:', e);
      showError('重置停滞任务失败', '错误');
    }
  };

  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page: filters.page,
        pageSize: filters.pageSize,
        search: filters.search || undefined,
        status: filters.status || undefined,
        userId: filters.userId || undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
      };

      const response = await tasksApi.getTasks(params);

      if (response.success && response.data) {
        if ('data' in response.data && Array.isArray(response.data.data)) {
          setTasks(response.data.data);
        } else if (Array.isArray(response.data)) {
          setTasks(response.data);
        } else {
          setTasks([]);
        }
      } else {
        showError(response.error?.message || '加载任务失败', '加载失败');
      }
    } catch (err) {
      console.error('Tasks load error:', err);
      showError('加载任务失败，请稍后重试', '加载失败');
    } finally {
      setLoading(false);
    }
  }, [filters, setTasks, setLoading]);

  useEffect(() => {
    const cacheIsValid = isCacheValid();
    const hasCachedData = tasks.length > 0;

    if (cacheIsValid && hasCachedData) {
      return;
    }

    loadTasks();
  }, [loadTasks, isCacheValid, tasks.length]);

  const handleViewDetails = (task: any) => {
    if (detailTask?.id === task.id) {
      setIsDetailOpen(false);
      setDetailTask(null);
    } else {
      setDetailTask(task);
      setIsDetailOpen(true);
    }
  };

  const handleSelectAll = () => {
    if (selectedTasks.size === tasks.length && tasks.length > 0) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(tasks.map(t => t.id)));
    }
  };

  const handleSelectTask = (taskId: string) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const handleBatchAction = async (action: 'retry' | 'cancel' | 'delete' | 'reset-stalled') => {
    if (selectedTasks.size === 0) return;

    if (action === 'delete') {
      showError(`确定要删除选中的 ${selectedTasks.size} 个任务吗？此操作不可恢复。`, '确认删除', 'CONFIRM_DELETE');
    } else if (action === 'cancel') {
      const cancellableTasks = tasks.filter(t => selectedTasks.has(t.id) && ['queued', 'downloading', 'uploading'].includes(t.status));
      if (cancellableTasks.length === 0) {
        showError('请选择排队中、下载中或上传中的任务进行取消', '提示');
        return;
      }
      showError(`确定要取消选中的 ${cancellableTasks.length} 个任务吗？此操作不可恢复。`, '确认取消', 'CONFIRM_CANCEL');
    } else if (action === 'retry') {
      const failedTasks = tasks.filter(t => selectedTasks.has(t.id) && t.status === 'failed');
      if (failedTasks.length === 0) {
        showError('请选择失败的任务进行重试', '提示');
        return;
      }
      showSuccess(`成功重试 ${failedTasks.length} 个任务`);
      await Promise.all(failedTasks.map(task => tasksApi.retryTask(task.id)));
      useTasksStore.getState().invalidateCache();
      loadTasks();
      setSelectedTasks(new Set());
    } else if (action === 'reset-stalled') {
      const stalledTaskIds = tasks
        .filter(t => selectedTasks.has(t.id) && ['downloading', 'uploading', 'queued'].includes(t.status))
        .map(t => t.id);
      if (stalledTaskIds.length === 0) {
        showError('请选择下载中、上传中或排队中的任务进行重置', '提示');
        return;
      }
      const resp = await tasksApi.resetStalledTasks(stalledTaskIds, 5 * 60 * 1000);
      if (resp.success) {
        showSuccess(`成功重置 ${resp.data?.count ?? stalledTaskIds.length} 个任务`);
        useTasksStore.getState().invalidateCache();
        loadTasks();
        setSelectedTasks(new Set());
      }
    }
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const params: Record<string, string | number | undefined> = {};
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;

      const blob = await tasksApi.exportCSV(params);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tasks_export_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
      } else {
        showError('导出失败', '导出结果');
      }
    } catch (err) {
      console.error('Export error:', err);
      showError('导出失败', '导出结果');
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleFilterChange = (key: keyof typeof filters, value: string | number | undefined) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      userId: '',
      startDate: undefined,
      endDate: undefined,
      page: 1,
      pageSize: 20,
    });
  };

  const hasActiveFilters = filters.search || filters.status || filters.userId || filters.startDate || filters.endDate;

  return (
    <div className="flex w-full overflow-hidden">
      <div className="flex-1 min-w-0 transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]">
        <PageHeader
          title="任务管理"
          actions={
            <div className="flex gap-3">
              <Button
                variant="default"
                onClick={handleExportCSV}
                disabled={exportLoading}
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {exportLoading ? '导出中...' : '导出 CSV'}
              </Button>
              <Button onClick={() => { useTasksStore.getState().invalidateCache(); loadTasks(); }}>
                <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setShowStalledPanel(!showStalledPanel)} title="停滞任务">
                <ExclamationTriangleIcon className="w-4 h-4" />
              </Button>
            </div>
          }
        />
        
        {showStalledPanel && (
          <div className="mb-6 bg-amber-500/10 rounded-2xl p-6 ring-1 ring-amber-500/20">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">停滞任务</h3>
                  <p className="text-sm text-white/60">超過 5 分鐘未更新的任务</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/60">{stalledTasks.length} 个任务</span>
                <Button variant="ghost" size="icon" onClick={loadStalledTasks}>
                  <ArrowPathIcon className={`h-4 w-4 ${loadingStalled ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>

            {loadingStalled ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-12 bg-white/5 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : stalledTasks.length === 0 ? (
              <div className="text-center py-8 text-white/40">没有停滞任务</div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {stalledTasks.map((task) => (
                  <div key={task.id} className="flex items-center justify-between bg-white/5 rounded-xl p-3 hover:bg-white/10 transition-colors">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={selectedStalledIds.has(task.id)}
                        onChange={(e) => toggleStalledSelect(task.id, e.target.checked)}
                        className="h-4 w-4 rounded border-white/20 bg-white/10"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{task.file_name}</p>
                        <p className="text-xs text-white/40">
                          {task.status} · {formatDate(task.updated_at)}
                        </p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleViewDetails(task)}>
                      查看详情
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {selectedStalledIds.size > 0 && (
              <div className="mt-4 flex justify-end gap-2 pt-4 border-t border-amber-500/20">
                <Button variant="ghost" size="sm" onClick={() => setSelectedStalledIds(new Set())}>
                  取消
                </Button>
                <Button
                  variant="warning"
                  size="sm"
                  onClick={handleResetSelected}
                  disabled={loadingStalled}
                >
                  重置 {selectedStalledIds.size} 个任务
                </Button>
              </div>
            )}
          </div>
        )}

        <SpotlightCard className="shadow-2xl backdrop-blur-md" enablePhysics={true}>
          <div className="relative z-20 p-6 bg-white/[0.01]">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/30 z-10" />
                <Input
                  type="text"
                  placeholder="搜索文件名..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-4 text-sm font-medium rounded-xl active:scale-95 transition-all h-[42px] ${
                  showFilters || hasActiveFilters
                    ? 'bg-white/20 text-white'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                <FunnelIcon className="w-4 h-4 mr-2" />
                筛选
                {hasActiveFilters && (
                  <span className="ml-2 w-2 h-2 bg-white rounded-full animate-pulse"></span>
                )}
              </button>
            </div>

            <Transition
              show={showFilters}
              as={Fragment}
              enter="transition duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
              enterFrom="transform scale-95 opacity-0 -translate-y-4"
              enterTo="transform scale-100 opacity-100 translate-y-0"
              leave="transition duration-300 ease-in"
              leaveFrom="transform scale-100 opacity-100 translate-y-0"
              leaveTo="transform scale-95 opacity-0 -translate-y-4"
            >
              <div className="mt-6 p-5 bg-white/[0.03] rounded-2xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {loading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="space-y-2">
                        <div className="h-3 bg-white/10 rounded w-1/3 animate-pulse" />
                        <div className="h-10 bg-white/10 rounded animate-pulse" />
                      </div>
                    ))
                  ) : (
                    <>
                      <Listbox
                        label="状态"
                        options={STATUS_OPTIONS}
                        value={filters.status}
                        onChange={(value) => handleFilterChange('status', value)}
                      />
                      
                      <Field className="space-y-2">
                        <Label className="block text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 ml-1">
                          用户 ID
                        </Label>
                        <Input
                          type="text"
                          value={filters.userId}
                          onChange={(e) => handleFilterChange('userId', e.target.value)}
                          placeholder="输入用户 ID"
                          className="py-2"
                        />
                      </Field>

                      <Field className="space-y-2">
                        <Label className="block text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 ml-1">
                          开始时间
                        </Label>
                        <Input
                          type="datetime-local"
                          value={filters.startDate ? new Date(filters.startDate).toISOString().slice(0, 16) : ''}
                          onChange={(e) => handleFilterChange('startDate', e.target.value ? new Date(e.target.value).getTime() : undefined)}
                          className="py-2 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-50"
                        />
                      </Field>

                      <Field className="space-y-2">
                        <Label className="block text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 ml-1">
                          结束时间
                        </Label>
                        <Input
                          type="datetime-local"
                          value={filters.endDate ? new Date(filters.endDate).toISOString().slice(0, 16) : ''}
                          onChange={(e) => handleFilterChange('endDate', e.target.value ? new Date(e.target.value).getTime() : undefined)}
                          className="py-2 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-50"
                        />
                      </Field>
                    </>
                  )}
                </div>
                {hasActiveFilters && !loading && (
                  <div className="mt-4 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                    >
                      <XMarkIcon className="w-4 h-4" />
                      清除所有筛选条件
                    </Button>
                  </div>
                )}
              </div>
            </Transition>
          </div>

          <div className="relative z-10 overflow-x-auto hidden sm:block">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white/[0.01]">
                  <th className="px-6 py-4 w-10">
                    <div 
                      onClick={handleSelectAll}
                      className={`w-5 h-5 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
                        selectedTasks.size === tasks.length && tasks.length > 0
                          ? 'bg-white text-black'
                          : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      {(selectedTasks.size === tasks.length && tasks.length > 0) && (
                        <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider">文件名</th>
                  <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider hidden md:table-cell">用户</th>
                  <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider">大小</th>
                  <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider">状态</th>
                  <th className="px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="">
                {loading && tasks.length === 0 ? (
                  <>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i}>
                        <td colSpan={6} className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            <div className="h-5 w-5 bg-white/10 rounded animate-pulse" />
                            <div className="flex-1 h-4 bg-white/10 rounded animate-pulse" />
                            <div className="hidden md:block w-24 h-4 bg-white/10 rounded animate-pulse" />
                            <div className="w-20 h-4 bg-white/10 rounded animate-pulse" />
                            <div className="w-24 h-6 bg-white/10 rounded-full animate-pulse" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                ) : tasks.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="text-white/30">暂无任务数据</div>
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr 
                      key={task.id} 
                      className={`hover:bg-white/[0.02] transition-colors group cursor-pointer ${detailTask?.id === task.id ? 'bg-white/5' : ''}`}
                      onClick={() => handleViewDetails(task)}
                    >
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <div 
                          onClick={() => handleSelectTask(task.id)}
                          className={`w-5 h-5 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
                            selectedTasks.has(task.id)
                              ? 'bg-white text-black'
                              : 'bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          {selectedTasks.has(task.id) && (
                            <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className={`text-sm font-semibold transition-colors truncate max-w-md ${detailTask?.id === task.id ? 'text-white' : 'text-white/90 group-hover:text-white'}`}>{task.file_name}</p>
                        <p className="text-[10px] text-white/30 font-mono mt-0.5">{task.id}</p>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <span className="text-sm text-white/50">{task.user_id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-white/50">{formatFileSize(task.file_size)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${getStatusColor(task.status)} bg-opacity-10 whitespace-nowrap shrink-0`}>
                          {getStatusText(task.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden">
            <InteractiveList
              items={tasks}
              loading={loading}
              emptyMessage="暂无任务数据"
              selectedItemId={detailTask?.id}
              onItemClick={handleViewDetails}
              renderItem={(task) => (
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div 
                        onClick={(e) => { e.stopPropagation(); handleSelectTask(task.id); }}
                        className={`w-5 h-5 shrink-0 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
                          selectedTasks.has(task.id)
                            ? 'bg-white text-black'
                            : 'bg-white/5'
                        }`}
                      >
                        {selectedTasks.has(task.id) && (
                          <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} />
                        )}
                      </div>
                      <div className="overflow-hidden">
                        <p className="text-sm font-semibold text-white/90 truncate">{task.file_name}</p>
                        <p className="text-xs text-white/40 mt-1">{formatDate(task.created_at)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-white/50">{formatFileSize(task.file_size)}</span>
                      <span className="text-white/20">·</span>
                      <span className="text-white/50 truncate max-w-[100px]">{task.user_id}</span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusColor(task.status)} bg-opacity-10 whitespace-nowrap shrink-0`}>
                      {getStatusText(task.status)}
                    </span>
                  </div>
                </div>
              )}
            />
          </div>

          {pagination.totalPages > 1 && (
            <div className="p-6 bg-white/[0.01] flex items-center justify-between">
              <div className="text-sm text-white/30">
                共 <span className="text-white/60">{pagination.total}</span> 条
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 1}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <div className="flex items-center gap-1 mx-2">
                  <span className="text-sm font-bold text-white">{filters.page}</span>
                  <span className="text-sm text-white/20">/</span>
                  <span className="text-sm text-white/40">{pagination.totalPages}</span>
                </div>
                <Button
                  size="icon"
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page === pagination.totalPages}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          )}
        </SpotlightCard>

        <Transition
          show={selectedTasks.size > 0}
          as={Fragment}
          enter="transition duration-200 ease-out"
          enterFrom="opacity-0 translate-y-8"
          enterTo="opacity-100 translate-y-0"
          leave="transition duration-150 ease-in"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-8"
        >
          <div className="fixed bottom-6 left-0 right-0 z-50 px-4 sm:px-0">
            <div className="mx-auto max-w-4xl bg-zinc-900/500 backdrop-blur-xl rounded-2xl shadow-inner shadow-white/10">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-3 sm:px-4">
                <div className="flex items-center gap-2 text-sm font-medium text-white/80 min-w-0 flex-shrink-0">
                  <span>已选择</span>
                  <span className="text-white font-bold truncate">{selectedTasks.size}</span>
                  <span className="hidden sm:inline">个任务</span>
                </div>
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 justify-center sm:justify-end overflow-x-auto flex-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleBatchAction('retry')}
                    className="flex-shrink-0 hover:bg-green-500/10 hover:text-green-400 transition-all duration-300 ease-in-out"
                  >
                    <ArrowUturnRightIcon className="h-4 w-4 text-green-400" />
                    <span className="hidden sm:inline ml-1">重试</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleBatchAction('cancel')}
                    className="flex-shrink-0 hover:bg-yellow-500/10 hover:text-yellow-400 transition-all duration-300 ease-in-out"
                  >
                    <XCircleIcon className="h-4 w-4 text-yellow-400" />
                    <span className="hidden sm:inline ml-1">停止</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleBatchAction('reset-stalled')}
                    title="重置停滞任务（下载中/上传中/排队中）"
                    className="flex-shrink-0 hover:bg-blue-500/10 hover:text-blue-400 transition-all duration-300 ease-in-out"
                  >
                    <ArrowPathIcon className="h-4 w-4 text-blue-400" />
                    <span className="hidden sm:inline ml-1">重置</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleBatchAction('delete')}
                    className="flex-shrink-0 hover:bg-red-500/10 hover:text-red-400 transition-all duration-300 ease-in-out"
                  >
                    <TrashIcon className="h-4 w-4 text-red-400" />
                    <span className="hidden sm:inline ml-1">删除</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedTasks(new Set())}
                    className="flex-shrink-0"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">取消</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>

      <TaskDetailPanel 
        isOpen={isDetailOpen} 
        onClose={() => setIsDetailOpen(false)} 
        task={detailTask}
      />
    </div>
  );
}